import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import { CompanyLogo } from "@/components/companies/company-logo";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
} from "@/components/collection";

import type { CompanyFacets } from "@/api/companies";

export interface CompanyListItem {
  slug: string;
  name: string;
  industry?: string;
  domain?: string;
  headquarters?: string;
  hqCountry?: string;
  logoUrl?: string;
  size?: string;
  jobCount?: number;
  isSeeded?: boolean;
  hasResearch?: boolean;
}

const ALLOWED_MODES = ["cards", "table", "list"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "name", order: "desc", label: "Name Z–A" },
  { field: "industry", order: "asc", label: "Industry" },
  { field: "jobCount", order: "desc", label: "Most jobs" },
];

function matchesSearch(item: CompanyListItem, query: string): boolean {
  const q = query.toLowerCase();
  return (
    item.name.toLowerCase().includes(q) ||
    (item.domain?.toLowerCase().includes(q) ?? false) ||
    (item.industry?.toLowerCase().includes(q) ?? false) ||
    (item.headquarters?.toLowerCase().includes(q) ?? false) ||
    (item.hqCountry?.toLowerCase().includes(q) ?? false)
  );
}

function applyToolbarSort(
  items: CompanyListItem[],
  sort: EntitySortOption,
): CompanyListItem[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "jobCount") {
      return ((a.jobCount ?? 0) - (b.jobCount ?? 0)) * dir;
    }
    if (sort.field === "industry") {
      return (a.industry ?? "").localeCompare(b.industry ?? "") * dir;
    }
    return a.name.localeCompare(b.name) * dir;
  });
}

interface CompanyDirectoryProps {
  items: CompanyListItem[];
  variant: "mine" | "directory";
  emptyMessage?: string;
  facets?: CompanyFacets;
  industry?: string;
  country?: string;
  onIndustryChange?: (value: string) => void;
  onCountryChange?: (value: string) => void;
}

export function CompanyDirectory({
  items,
  variant,
  emptyMessage = "No companies match your search.",
  facets,
  industry: industryProp,
  country: countryProp,
  onIndustryChange,
  onCountryChange,
}: CompanyDirectoryProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [localIndustry, setLocalIndustry] = useState("");
  const [localCountry, setLocalCountry] = useState("");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: `viewMode_companies_${variant}`,
    defaultMode: "cards",
    allowedModes: [...ALLOWED_MODES],
  });

  const industry = industryProp ?? localIndustry;
  const country = countryProp ?? localCountry;
  const isControlled =
    onIndustryChange !== undefined || onCountryChange !== undefined;

  const setIndustry = (value: string) => {
    if (onIndustryChange) onIndustryChange(value);
    else setLocalIndustry(value);
  };
  const setCountry = (value: string) => {
    if (onCountryChange) onCountryChange(value);
    else setLocalCountry(value);
  };

  const industries = useMemo(() => {
    if (facets?.industries.length) return facets.industries;
    const set = new Set<string>();
    for (const item of items) {
      if (item.industry) set.add(item.industry);
    }
    return Array.from(set).sort();
  }, [items, facets]);

  const countries = useMemo(() => {
    if (facets?.countries.length) return facets.countries;
    const map = new Map<string, string>();
    for (const item of items) {
      if (!item.hqCountry) continue;
      const slug = item.hqCountry
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      map.set(slug, item.hqCountry);
    }
    return Array.from(map.entries())
      .map(([slug, name]) => ({ slug, name, count: 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, facets]);

  const filtered = useMemo(() => {
    const list = items.filter((item) => {
      if (!isControlled && industry && item.industry !== industry) return false;
      if (!isControlled && country) {
        const slug = item.hqCountry
          ?.toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        if (slug !== country) return false;
      }
      if (search.trim() && !matchesSearch(item, search.trim())) return false;
      return true;
    });
    return applyToolbarSort(list, sortOption);
  }, [items, search, industry, country, isControlled, sortOption]);

  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filtered, [
    search,
    industry,
    country,
    viewMode,
    sortOption.field,
    sortOption.order,
  ]);

  const getSortValue = useCallback((item: CompanyListItem, key: string) => {
    if (key === "jobCount") return item.jobCount ?? 0;
    if (key === "industry") return item.industry ?? "";
    if (key === "hq") {
      return (
        [item.hqCountry, item.headquarters].filter(Boolean).join(", ") ||
        item.domain ||
        ""
      );
    }
    if (key === "size" || key === "status") {
      return variant === "mine"
        ? item.isSeeded
          ? "directory"
          : ""
        : (item.size ?? "");
    }
    return item.name;
  }, [variant]);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pageItems,
      defaultKey: null,
      getValue: getSortValue,
    });

  const filterFields = useMemo((): EntityFilterSelectField[] => {
    const fields: EntityFilterSelectField[] = [];
    if (industries.length > 0) {
      fields.push({
        id: "industry",
        label: "Industry",
        value: industry,
        allLabel: "All industries",
        options: industries.map((ind) => ({ value: ind, label: ind })),
        onChange: setIndustry,
      });
    }
    if (countries.length > 0) {
      fields.push({
        id: "country",
        label: "Country",
        value: country,
        allLabel: "All countries",
        options: countries.map((c) => ({
          value: c.slug,
          label: c.count > 0 ? `${c.name} (${c.count})` : c.name,
        })),
        onChange: setCountry,
      });
    }
    return fields;
  }, [industries, countries, industry, country]);

  const activeFilterCount = (industry ? 1 : 0) + (country ? 1 : 0);

  function goToCompany(slug: string) {
    navigate(`/companies/${slug}`);
  }

  const columns = useMemo((): Column<CompanyListItem & { id: string }>[] => {
    return [
      {
        key: "logo",
        header: "",
        className: "w-12",
        render: (item) => (
          <CompanyLogo
            name={item.name}
            logoUrl={item.logoUrl}
            domain={item.domain}
            size="sm"
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        sortable: true,
        render: (item) => (
          <span className="font-medium text-foreground">{item.name}</span>
        ),
      },
      {
        key: "industry",
        header: "Industry",
        sortable: true,
        render: (item) => (
          <span className="text-muted-foreground">{item.industry ?? "—"}</span>
        ),
      },
      {
        key: variant === "mine" ? "jobCount" : "hq",
        header: variant === "mine" ? "Jobs" : "HQ",
        sortable: true,
        render: (item) => (
          <span className="text-muted-foreground">
            {variant === "mine"
              ? (item.jobCount ?? 0) > 0
                ? `${item.jobCount} job${(item.jobCount ?? 0) !== 1 ? "s" : ""}`
                : "—"
              : [item.hqCountry, item.headquarters].filter(Boolean).join(", ") ||
                item.domain ||
                "—"}
          </span>
        ),
      },
      {
        key: variant === "mine" ? "status" : "size",
        header: variant === "mine" ? "Status" : "Size",
        sortable: true,
        render: (item) => (
          <span className="text-muted-foreground">
            {variant === "mine" ? (
              item.isSeeded ? (
                <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-xs text-sky-400">
                  Directory
                </span>
              ) : (
                "—"
              )
            ) : (
              item.size ?? "—"
            )}
          </span>
        ),
      },
    ];
  }, [variant]);

  const tableRows = sortedItems.map((item) => ({ ...item, id: item.slug }));

  return (
    <div>
      <div className="mb-4">
        <CollectionCommandBar
          searchQuery={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name, domain, industry…"
          sortOption={sortOption}
          onSortChange={setSortOption}
          sortOptions={SORT_OPTIONS}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          allowedModes={allowedModes}
          filterFields={filterFields}
          activeFilterCount={activeFilterCount}
          onClear={() => {
            setSearch("");
            setIndustry("");
            setCountry("");
          }}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          {filtered.length === 0
            ? `0 of ${items.length}`
            : `Showing ${showingFrom}–${showingTo} of ${filtered.length}`}
        </p>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      ) : (
        <EntityListLayout
          viewMode={viewMode}
          cards={
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((item) => (
                <EntityCard
                  key={item.slug}
                  id={item.slug}
                  title={item.name}
                  icon={
                    <CompanyLogo
                      name={item.name}
                      logoUrl={item.logoUrl}
                      domain={item.domain}
                      size="md"
                    />
                  }
                  primaryBadge={
                    item.isSeeded && variant === "mine"
                      ? {
                          label: "Directory",
                          colorClass: "bg-sky-500/10 text-sky-400",
                        }
                      : undefined
                  }
                  secondaryBadge={
                    item.hasResearch
                      ? {
                          label: "Research",
                          colorClass: "bg-emerald-500/10 text-emerald-400",
                        }
                      : undefined
                  }
                  description={item.industry}
                  metadata={[
                    {
                      value: [item.hqCountry, item.headquarters, item.domain]
                        .filter(Boolean)
                        .join(" · "),
                    },
                    ...(variant === "mine" && (item.jobCount ?? 0) > 0
                      ? [
                          {
                            value: `${item.jobCount} job${(item.jobCount ?? 0) !== 1 ? "s" : ""}`,
                          },
                        ]
                      : []),
                  ].filter((m) => m.value)}
                  onClick={() => goToCompany(item.slug)}
                />
              ))}
            </div>
          }
          list={
            <div className="space-y-2">
              {pageItems.map((item) => (
                <EntityCompactItem
                  key={item.slug}
                  id={item.slug}
                  title={item.name}
                  icon={
                    <CompanyLogo
                      name={item.name}
                      logoUrl={item.logoUrl}
                      domain={item.domain}
                      size="sm"
                    />
                  }
                  description={
                    [item.industry, item.hqCountry, item.domain ?? item.headquarters]
                      .filter(Boolean)
                      .join(" · ") || "—"
                  }
                  badges={
                    item.isSeeded && variant === "mine"
                      ? [
                          {
                            label: "Directory",
                            colorClass: "bg-sky-500/10 text-sky-400",
                          },
                        ]
                      : undefined
                  }
                  metadata={
                    variant === "mine" && (item.jobCount ?? 0) > 0
                      ? [
                          {
                            value: `${item.jobCount} job${(item.jobCount ?? 0) !== 1 ? "s" : ""}`,
                          },
                        ]
                      : undefined
                  }
                  onClick={() => goToCompany(item.slug)}
                />
              ))}
            </div>
          }
          table={
            <DataTable
              data={tableRows}
              columns={columns}
              onRowClick={(item) => goToCompany(item.slug)}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              emptyMessage={emptyMessage}
            />
          }
        />
      )}

      {filtered.length > 0 && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          totalItems={filtered.length}
          totalPages={totalPages}
          showingFrom={showingFrom}
          showingTo={showingTo}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
}

/** Normalize merged user company view into list item shape. */
export function mergedToListItem(merged: {
  slug: string;
  name: string;
  isSeeded?: boolean;
  jobCount?: number;
  global?: {
    industry?: string;
    domain?: string;
    headquarters?: string;
    hqCountry?: string;
    logoUrl?: string;
    size?: string;
    research?: string;
  };
  user?: {
    research?: string;
  };
}): CompanyListItem {
  return {
    slug: merged.slug,
    name: merged.name,
    industry: merged.global?.industry,
    domain: merged.global?.domain,
    headquarters: merged.global?.headquarters,
    hqCountry: merged.global?.hqCountry,
    logoUrl: merged.global?.logoUrl,
    size: merged.global?.size,
    jobCount: merged.jobCount,
    isSeeded: merged.isSeeded,
    hasResearch: Boolean(merged.user?.research || merged.global?.research),
  };
}

/** Normalize global company into list item shape. */
export function globalToListItem(company: {
  slug: string;
  name: string;
  industry?: string;
  domain?: string;
  headquarters?: string;
  hqCountry?: string;
  logoUrl?: string;
  size?: string;
}): CompanyListItem {
  return {
    slug: company.slug,
    name: company.name,
    industry: company.industry,
    domain: company.domain,
    headquarters: company.headquarters,
    hqCountry: company.hqCountry,
    logoUrl: company.logoUrl,
    size: company.size,
    isSeeded: true,
  };
}
